import type { SiteListItem } from '@tabel/shared/schemas/sites';
import { cityLabel } from '@tabel/shared/sites/cities';
import { SITE_TEXTS, siteStateBadge } from '@tabel/shared/sites/step-texts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton } from '../../../components/ui';
import { useSetCrumbs } from '../../../lib/crumbs';
import { reportClientError } from '../../../lib/errors';
import { ruDateTime } from '../../../lib/format';
import { SETTINGS_PATH } from '../../../lib/nav';
import { useRole } from '../../../lib/role';
import { sitesKeys, sitesList } from '../api';
import { settingsCrumbs } from '../crumbs';
import { LoadError, Refused, SCREEN, isForbidden, shouldReport } from '../parts';
import { LIST } from '../texts';
import { CreateSiteDialog } from './CreateSiteDialog';

/*
 * Раздел «Площадки» (007 T039; spec.md FR-002, US1 сценарий 1; site-onboarding-design.md раздел 2; прототип
 * prototypes/site-onboarding.html, `viewList`): раздел-список во всю ширину. Таблица `t-tbl-stack` — на телефоне строки
 * становятся карточками: название и способ связи сверху, свойства ниже. Способ «напрямую» помечен «без шифрования»
 * (поправка конституции 19.10). Состояние — бейдж по `siteStateBadge`. Справа от заголовка — главная кнопка
 * «Подключить площадку»: окно шага 1, после создания — страница новой площадки. Показывается только то, что прислал
 * сервер (`GET /api/sites`); права проверяет сервер, раздел виден только администратору системы (`lib/nav.ts`).
 */

export function SitesSection() {
  useSetCrumbs(settingsCrumbs({ label: LIST.title }));
  const { role } = useRole();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: sitesKeys.list(role), queryFn: sitesList });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'list', q.error);
  }, [q.error]);

  let body: ReactNode;
  if (q.isError) {
    body = isForbidden(q.error) ? (
      <Refused title={LIST.forbiddenTitle} text={LIST.forbidden} error={q.error} />
    ) : (
      <LoadError
        title={LIST.loadError}
        error={q.error}
        busy={q.isFetching}
        onRetry={() => void q.refetch()}
      />
    );
  } else if (!q.data) body = <Loading />;
  else if (q.data.length === 0)
    body = (
      <Card>
        <EmptyState icon={MapPin} title={LIST.emptyTitle} description={LIST.emptyText} />
      </Card>
    );
  else body = <SitesTable sites={q.data} onOpen={(code) => navigate(SETTINGS_PATH.site(code))} />;

  return (
    <>
      <PageHeader
        title={LIST.title}
        description={LIST.description}
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            {LIST.add}
          </Button>
        }
      />
      <div className="mt-6">{body}</div>
      <CreateSiteDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(d) => {
          setCreating(false);
          void qc.invalidateQueries({ queryKey: sitesKeys.all });
          navigate(SETTINGS_PATH.site(d.site.code));
        }}
      />
    </>
  );
}

function Loading() {
  return (
    <Card className="space-y-3 p-4" aria-busy="true" aria-label="Загрузка…">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-5 w-1/2" />
    </Card>
  );
}

/**
 * Таблица площадок (дизайн-система DataTable, `t-tbl-stack`): первая колонка закреплена, числа — по правому краю.
 * Строка открывает страницу площадки нажатием; с клавиатуры — ссылка с названием. На телефоне шапки нет, поэтому у
 * чисел есть подписи (`sm:hidden`).
 */
function SitesTable({ sites, onOpen }: { sites: readonly SiteListItem[]; onOpen: (code: string) => void }) {
  return (
    <Card className="overflow-x-auto">
      <table className="t-tbl t-tbl-stack" aria-label={LIST.title}>
        <thead>
          <tr>
            <th className="sticky">{LIST.columns.site}</th>
            <th>{LIST.columns.city}</th>
            <th className="num">{LIST.columns.terminals}</th>
            <th>{LIST.columns.last}</th>
            <th>{LIST.columns.state}</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((s) => {
            const badge = siteStateBadge(s);
            return (
              // biome-ignore lint/a11y/useKeyWithClickEvents: с клавиатуры строку открывает ссылка с названием площадки
              <tr key={s.code} className="cursor-pointer" onClick={() => onOpen(s.code)}>
                <td className="sticky min-w-[140px] whitespace-normal">
                  {/* Название и пометка «без шифрования» — строкой, способ связи — под ними: колонка не шире нужного. */}
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Link
                      to={SETTINGS_PATH.site(s.code)}
                      className="font-medium text-ink hover:text-accent"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {s.name}
                    </Link>
                    {s.unencrypted && <Badge tone="warn">{SITE_TEXTS.unencrypted}</Badge>}
                  </span>
                  <span className="mt-0.5 block text-[12px] font-normal text-ink-2">
                    {SITE_TEXTS.linkMode[s.linkMode]}
                  </span>
                </td>
                <td>{cityLabel(s.city, s.tzOffset)}</td>
                <td className="num">
                  <span className="sm:hidden">{LIST.phone.terminals} </span>
                  {s.terminals}
                </td>
                <td className="t-num">
                  <span className="sm:hidden">{LIST.phone.last} </span>
                  {ruDateTime(s.lastPassAt, s.tzOffset)}
                </td>
                <td>
                  <Badge tone={badge.tone} dot>
                    {badge.text}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
